import React, { useState, useRef, useEffect } from 'react';
import { plantsAPI, externalAPI, imageAPI } from '../services/api';
import './AIPlantLens.css';

function AIPlantLens({ isOpen, onClose, onAddToInventory, onAddToWishlist }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [identifying, setIdentifying] = useState(false);
  const [identificationResult, setIdentificationResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Clean up blob URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      // Revoke old blob URL before creating new one
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
      setIdentificationResult(null);
    }
  };

  const handleIdentify = async () => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }

    try {
      setIdentifying(true);
      setError('');
      
      // First, identify the plant
      const response = await plantsAPI.identify(selectedImage);
      
      if (response.data.success) {
        const result = response.data;
        
        // Upload the user's image to server for permanent storage
        try {
          const uploadResponse = await imageAPI.upload(selectedImage);
          if (uploadResponse.data.success) {
            // Use the uploaded image URL instead of Pl@ntNet URL
            result.uploadedImageUrl = uploadResponse.data.imageUrl;
            console.log('[AI Plant Lens] Image uploaded successfully:', uploadResponse.data.imageUrl);
          }
        } catch (uploadErr) {
          console.error('[AI Plant Lens] Failed to upload image:', uploadErr);
          // Continue even if upload fails - we can still use Pl@ntNet URL or default
        }
        
        // Fetch care tips and soil mix information
        try {
          const [careTipsRes, soilMixRes] = await Promise.all([
            externalAPI.searchCareTips(result.name).catch(() => ({ data: { careTips: [] } })),
            externalAPI.searchSoilMix(result.name).catch(() => ({ data: { soilMixInfo: [] } }))
          ]);

          const careTips = careTipsRes.data.careTips || [];
          const soilMixInfo = soilMixRes.data.soilMixInfo || [];

          // Format care tips
          const careText = careTips.length > 0
            ? careTips.map(tip => tip.snippet).filter(Boolean).join('\n\n')
            : '';

          // Format soil mix info
          const soilText = soilMixInfo.length > 0
            ? soilMixInfo.map(info => info.snippet).filter(Boolean).join('\n\n')
            : '';

          // Add care and soil info to result
          result.careNotes = careText;
          result.soilMixNotes = soilText;
        } catch (err) {
          console.error('Failed to fetch additional plant information:', err);
          // Continue even if external data fetch fails
        }
        
        setIdentificationResult(result);
      } else {
        setError(response.data.message || 'Could not identify the plant. Please try a different image.');
      }
    } catch (err) {
      console.error('Error identifying plant:', err);
      setError('Failed to identify plant. Please try again.');
    } finally {
      setIdentifying(false);
    }
  };

  const handleAddToInventory = () => {
    if (identificationResult) {
      onAddToInventory({
        name: identificationResult.name,
        species: identificationResult.species,
        // Priority: 1. Uploaded image, 2. Pl@ntNet image, 3. Empty (default placeholder)
        imageUrl: identificationResult.uploadedImageUrl || identificationResult.imageUrl || '',
        category: 'Other',
        careNotes: identificationResult.careNotes || '',
        soilMixNotes: identificationResult.soilMixNotes || ''
      });
      handleClose();
    }
  };

  const handleAddToWishlist = () => {
    if (identificationResult) {
      onAddToWishlist({
        name: identificationResult.name,
        species: identificationResult.species,
        // Priority: 1. Uploaded image, 2. Pl@ntNet image, 3. Empty (default placeholder)
        imageUrl: identificationResult.uploadedImageUrl || identificationResult.imageUrl || '',
        category: 'Other',
        wishlist: true,
        careNotes: identificationResult.careNotes || '',
        soilMixNotes: identificationResult.soilMixNotes || ''
      });
      handleClose();
    }
  };

  const handleSnapAnother = () => {
    // Revoke blob URL before clearing
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedImage(null);
    setPreviewUrl(null);
    setIdentificationResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleSnapAnother();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="ai-lens-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="ai-lens-title">
      <div className="ai-lens-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-lens-header">
          <h2 id="ai-lens-title">🌿 AI Plant Lens</h2>
          <button className="close-button" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ai-lens-content">
          {!selectedImage && (
            <div className="upload-section">
              <div className="upload-prompt">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                </svg>
                <p>Snap or upload a photo of any plant</p>
                <label htmlFor="plant-image-input" className="upload-button">
                  Choose Image
                </label>
                <input
                  id="plant-image-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <p className="upload-hint">Supports JPG, PNG (max 5MB)</p>
              </div>
            </div>
          )}

          {selectedImage && !identificationResult && (
            <div className="preview-section">
              <div className="image-preview">
                <img src={previewUrl} alt="Selected plant" />
              </div>
              <button 
                className="btn btn-primary identify-button"
                onClick={handleIdentify}
                disabled={identifying}
              >
                {identifying ? 'Identifying...' : '🔍 Identify Plant'}
              </button>
            </div>
          )}

          {identificationResult && (
            <div className="result-section">
              <div className="image-preview">
                <img src={previewUrl} alt="Identified plant" />
              </div>
              <div className="identification-result">
                <h3>{identificationResult.name}</h3>
                <p className="scientific-name">{identificationResult.scientificName}</p>
                <p className="family-info">Family: {identificationResult.family}</p>
                <div className="confidence-badge">
                  Confidence: {identificationResult.confidence}%
                </div>
              </div>
              <div className="action-buttons">
                <button className="btn btn-success" onClick={handleAddToInventory}>
                  ✓ Add to Inventory
                </button>
                <button className="btn btn-wishlist" onClick={handleAddToWishlist}>
                  ♡ Add to Wishlist
                </button>
                <button className="btn btn-secondary" onClick={handleSnapAnother}>
                  📷 Snap Another
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
              {selectedImage && (
                <button className="btn btn-secondary" onClick={handleSnapAnother}>
                  Try Another Image
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIPlantLens;
